import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date.js';
import '../../library/editors/elements/d-edit-text.js';
import '../../library/editors/elements/d-edit-textarea.js';
import '../../library/editors/elements/d-select-tag.js';
import '../../library/elements/d-action.js';
import '../../library/elements/d-label.js';

import '../../library/elements/d-section.js';
import '../../library/elements/d-wrap.js';
import '../../library/fields/d-view-info.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { LocalDateTime } from 'src/utilities/local-date-time.js';
import type { ShareStaffingInput } from './share-staffing-input';
import type { ShareVacationResult } from './share-vacation-result';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
import { isEmailValid } from '../../store/selectors/utilities/isEmailValid';
import { t } from 'src/i18n';

/**
 *
 * USAGE:
 *    d-share-staffing
 *
 * STATUS OK
 */

@customElement('share-staffing-dialog')
export class ShareStaffingDialog extends BaseDialog<ShareStaffingInput, ShareVacationResult> {
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      .shares > * {
        margin-bottom: 6px;
      }
    `,
  ];

  @property({ type: Number })
  currentYear = 1900;
  @property({ type: Array })
  employeesOptions: SelectTagOption[] = [];
  @property({ type: Array })
  employeesOrder: string[] = [];
  @property({ type: Array })
  selectedEmployees: string[] = [];
  @property({ type: String })
  notes = '';
  @property({ type: String })
  email = '';
  /**
   * Epostadressene som planen er delt med. For tiden kan dette kun være 1 eller ingen
   */
  @property({ type: Array })
  shares: { email: string; time: string }[] = [];
  @property({ type: Boolean })
  currentUserHasAccess = false;
  @property({ type: Number })
  organizationId = 0;
  @property({ type: String })
  token = '';
  headerActions = [{ name: t('staffing.closeLowercase'), action: 'close' }];
  width = 600;
  private get info() {
    return t('staffing.shareVacationInfo');
  }

  protected get calculatedTitle(): string {
    return t('staffing.shareVacationTitle', { year: this.currentYear });
  }

  _sharedWithLabel(shares) {
    if (shares.length) {
      return t('staffing.sharedWith');
    }
    return t('staffing.shareWith');
  }

  _showEmailInput(shares) {
    return shares.length < 1;
  }

  _timeDisplay(time) {
    if (time) {
      const dateString = time.split(' ')[0];
      const timeString = time.split(' ')[1];
      return t('staffing.timeDisplayAtClock', {
        date: LocalDate.fromString(dateString).toStringForDisplay(),
        time: timeString.replace(':', '.'),
      });
    }
    return '';
  }

  _stringIsEmpty(value) {
    return value ? value.trim().length === 0 : true;
  }

  _isEmail(email) {
    return !this._stringIsEmpty(email) && isEmailValid(email);
  }

  _share(e: Event) {
    e.stopPropagation();
    if (this._isEmail(this.email)) {
      const c: ShareVacationResult = {
        recipient: this.email,
        notes: this.notes,
        employees: sortSelectedEmployees(this.selectedEmployees, this.employeesOrder),
      };
      this.dispatchEvent(
        new CustomEvent<ShareVacationResult>('share-vacation', { composed: true, bubbles: true, detail: c }),
      );
      this.shares = [{ email: this.email, time: LocalDateTime.now().toString() }];
    }
  }

  _unshare(e: Event, email: string) {
    e.preventDefault();
    e.stopPropagation();
    const c = {
      recipient: email,
    };
    this.dispatchEvent(
      new CustomEvent<{
        recipient: string;
      }>('unshare-vacation', { composed: true, bubbles: true, detail: c }),
    );
    this.email = '';
    this.shares = [];
  }

  _showShare(e: Event, email: string) {
    e.preventDefault();
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent<{ email: string }>('show-share-vacation', {
        composed: true,
        bubbles: true,
        detail: { email: email },
      }),
    );
  }

  _reshare(e: Event, email: string) {
    e.preventDefault();
    e.stopPropagation();
    const c = {
      recipient: email,
      notes: this.notes,
      employees: sortSelectedEmployees(this.selectedEmployees, this.employeesOrder),
    };
    this.dispatchEvent(
      new CustomEvent<ShareVacationResult>('share-vacation', { composed: true, bubbles: true, detail: c }),
    );
    this.shares = [{ email: this.email, time: LocalDateTime.now().toString() }];
  }

  renderBody() {
    const showEmailInput = this._showEmailInput(this.shares);
    return html`
      <d-section>
        <d-view-info .content=${this.info}></d-view-info>
      </d-section>
      <d-section>
        <d-select-tag
          label="${t('staffing.shareVacationFor')}"
          .options=${this.employeesOptions}
          .value=${this.selectedEmployees}
          @value-changed=${this.onSelectedEmployeesChanged}
          toggle-all
        ></d-select-tag>
      </d-section>
      <d-section>
        <d-edit-textarea
          label="${t('staffing.vacationCommentsLabel')}"
          placeholder="${t('staffing.vacationCommentsPlaceholder')}"
          .value=${this.notes}
          max-rows="30"
          @value-changed=${this.onNotesChanged}
        >
        </d-edit-textarea>
      </d-section>
      <d-section>
        <div>
          <d-label label="${this._sharedWithLabel(this.shares)}"></d-label>
          <div class="shares">
            ${this.shares.map(
              (item) =>
                html` <d-wrap split>
                    <div>${item.email}</div>
                    <div class="comment">${this._timeDisplay(item.time)}</div>
                  </d-wrap>
                  <d-wrap split>
                    <d-action plain @click=${(e) => this._showShare(e, item.email)}
                      >${t('staffing.showShare')}</d-action
                    >
                    <d-wrap right>
                      <d-action plain @click=${(e) => this._unshare(e, item.email)}
                        >${t('staffing.stopShare')}</d-action
                      >
                      <d-action plain @click=${(e) => this._reshare(e, item.email)}>${t('staffing.reshare')}</d-action>
                    </d-wrap>
                  </d-wrap>`,
            )}
          </div>
          ${showEmailInput
            ? html`
                <d-edit-text
                  placeholder="${t('staffing.emailAddressPlaceholder')}"
                  .value=${this.email}
                  button
                  button-text="${t('staffing.share')}"
                  ?button-disabled=${!this._isEmail(this.email)}
                  @button-click=${this._share}
                  @value-changed=${this.onEmailChanged}
                ></d-edit-text>
              `
            : nothing}
        </div>
      </d-section>
    `;
  }

  protected fetchResult(_detail: string | undefined): ShareVacationResult {
    return {
      recipient: this.email,
      notes: this.notes,
      employees: sortSelectedEmployees(this.selectedEmployees, this.employeesOrder),
    };
  }

  protected initializeDialog(input: ShareStaffingInput) {
    this.currentYear = input.currentYear;
    this.email = input.email;
    this.employeesOptions = input.employeesOptions;
    this.employeesOrder = input.employeesOrder;
    this.notes = input.notes;
    this.selectedEmployees = input.selectedEmployees;
    if (input.email !== '') {
      this.shares = [{ email: input.email, time: input.time }];
    }
  }

  private onSelectedEmployeesChanged(e: CustomEvent<{ value: string[] }>) {
    e.stopPropagation();
    this.selectedEmployees = e.detail.value;
  }

  private onNotesChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.notes = e.detail.value;
  }

  private onEmailChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.email = e.detail.value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'share-staffing-dialog': ShareStaffingDialog;
  }
}

export function sortSelectedEmployees(selectedEmployees: string[], employeesOrder: string[]): string[] {
  if (employeesOrder.length === 0) {
    return selectedEmployees;
  }

  const selected = new Set(selectedEmployees);
  const ordered = employeesOrder.filter((uuid) => selected.has(uuid));
  const unordered = selectedEmployees.filter((uuid) => !employeesOrder.includes(uuid));
  return ordered.concat(unordered);
}
